import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSEO } from '../hooks/useSEO';
export function PrivacyPolicy() {
  useSEO({
    title: 'Privacy Policy',
    description: 'How 888 Sourcing Ltd collects, uses, and protects personal and business data in accordance with the UK General Data Protection Regulation (UK GDPR).',
    path: '/privacy-policy'
  });
  return <div className="pt-24 pb-24 bg-navy-dark min-h-screen" data-id="element-526">
      {/* Hero Band */}
      <section className="py-16 px-6 bg-navy-light border-b border-slate-800" data-id="element-527">
        <div className="max-w-4xl mx-auto text-center" data-id="element-528">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-529">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-530">
              <div className="w-12 h-px bg-gold" data-id="element-531"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-532">
                Legal
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-533"></div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-semibold text-white mb-4" data-id="element-534">
              Privacy Policy
            </h1>
            <p className="text-slate-400 text-sm" data-id="element-535">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-GB', {
              year: 'numeric',
              month: 'long'
            })}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6" data-id="element-536">
        <div className="max-w-3xl mx-auto" data-id="element-537">
          <div className="prose prose-invert max-w-none space-y-8" data-id="element-538">
            <div data-id="element-539">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-540">
                1. Introduction
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-541">
                888 Sourcing Ltd ("we", "us", "our") is a UK-registered
                procurement consultancy (Companies House No. 08400329, VAT No.
                GB158689350) with its registered office in London, United
                Kingdom. This Privacy Policy explains how we collect, use,
                store, and protect personal data in accordance with the UK
                General Data Protection Regulation (UK GDPR) and the Data
                Protection Act 2018.
              </p>
            </div>

            <div data-id="element-542">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-543">
                2. Data Controller
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-544">
                888 Sourcing Ltd is the data controller responsible for personal
                data collected through this website and any related business
                engagements.
              </p>
            </div>

            <div data-id="element-545">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-546">
                3. Information We Collect
              </h2>
              <p className="text-slate-400 leading-relaxed mb-3" data-id="element-547">
                We collect the following categories of personal and business
                data:
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-548">
                <li data-id="element-549">
                  Contact information (name, job title, email, phone number,
                  address)
                </li>
                <li data-id="element-550">
                  Company information (legal name, registration number, VAT/tax
                  ID, website, country of registration)
                </li>
                <li data-id="element-551">
                  Commercial information (products, capabilities, capacity,
                  certifications, commercial terms)
                </li>
                <li data-id="element-552">
                  Documents you upload (company profiles, catalogues,
                  certifications, price lists)
                </li>
                <li data-id="element-553">
                  Communications with our team (emails, enquiries, messages)
                </li>
              </ul>
            </div>

            <div data-id="element-554">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-555">
                4. How We Use Your Data
              </h2>
              <p className="text-slate-400 leading-relaxed mb-3" data-id="element-556">
                We process your data for the following purposes:
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-557">
                <li data-id="element-558">Supplier evaluation, verification, and onboarding</li>
                <li data-id="element-559">
                  Matching suppliers with relevant procurement opportunities
                </li>
                <li data-id="element-560">Coordinating tender submissions and contract liaison</li>
                <li data-id="element-561">
                  Responding to enquiries and providing requested information
                </li>
                <li data-id="element-562">
                  Communicating about procurement opportunities you have
                  consented to receive
                </li>
                <li data-id="element-563">
                  Complying with legal, regulatory, and contractual obligations
                </li>
              </ul>
            </div>

            <div data-id="element-564">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-565">
                5. Lawful Basis for Processing
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-566">
                We process personal data on the basis of your consent (where
                given), the performance of a contract or pre-contractual steps,
                our legitimate business interests in operating a procurement
                consultancy, and our legal obligations as a UK-registered
                company.
              </p>
            </div>

            <div data-id="element-567">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-568">
                6. Data Sharing
              </h2>
              <p className="text-slate-400 leading-relaxed mb-3" data-id="element-569">
                We may share your data with:
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-570">
                <li data-id="element-571">
                  Procurement entities and buyers, where relevant to a specific
                  opportunity and only with your knowledge
                </li>
                <li data-id="element-572">
                  Trusted service providers (IT, hosting, email, accounting)
                  under appropriate contractual safeguards
                </li>
                <li data-id="element-573">Regulatory authorities, where required by law</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-3" data-id="element-574">
                We do not sell personal data to third parties.
              </p>
            </div>

            <div data-id="element-575">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-576">
                7. International Transfers
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-577">
                As a global procurement consultancy, we may transfer your data
                outside the United Kingdom and European Economic Area to
                facilitate procurement activities. Where we do so, we ensure
                appropriate safeguards are in place, including UK and EU
                Standard Contractual Clauses where applicable.
              </p>
            </div>

            <div data-id="element-578">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-579">
                8. Data Retention
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-580">
                We retain supplier registration data for as long as your company
                remains an active or potential supplier in our network, and for
                up to seven years thereafter to comply with UK accounting and
                regulatory obligations. Enquiry data is retained for up to three
                years.
              </p>
            </div>

            <div data-id="element-581">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-582">
                9. Your Rights
              </h2>
              <p className="text-slate-400 leading-relaxed mb-3" data-id="element-583">
                Under UK GDPR, you have the right to:
              </p>
              <ul className="list-disc list-inside text-slate-400 space-y-2 ml-4" data-id="element-584">
                <li data-id="element-585">Access the personal data we hold about you</li>
                <li data-id="element-586">Request correction of inaccurate or incomplete data</li>
                <li data-id="element-587">
                  Request erasure of your data, subject to legal retention
                  requirements
                </li>
                <li data-id="element-588">Object to or restrict processing</li>
                <li data-id="element-589">Request data portability</li>
                <li data-id="element-590">Withdraw consent at any time</li>
                <li data-id="element-591">
                  Lodge a complaint with the UK Information Commissioner's
                  Office (ICO)
                </li>
              </ul>
            </div>

            <div data-id="element-592">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-593">
                10. Security
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-594">
                We implement appropriate technical and organisational measures
                to protect your data against unauthorised access, alteration,
                disclosure, or destruction.
              </p>
            </div>

            <div data-id="element-595">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-596">
                11. Cookies
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-597">
                Our website uses essential cookies necessary for site
                functionality. We do not currently use analytics or marketing
                cookies. If this changes, we will update this policy and request
                appropriate consent.
              </p>
            </div>

            <div data-id="element-598">
              <h2 className="text-2xl font-semibold text-white mb-4" data-id="element-599">
                12. Contact
              </h2>
              <p className="text-slate-400 leading-relaxed" data-id="element-600">
                For any privacy-related enquiries, including to exercise your
                rights, please contact us at{' '}
                <a href="mailto:info@888sourcing.co.uk" className="text-gold hover:underline" data-id="element-601">
                  info@888sourcing.co.uk
                </a>{' '}
                or via our{' '}
                <Link to="/contact" className="text-gold hover:underline" data-id="element-602">
                  Contact page
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}